/* eslint-disable no-param-reassign */
/* eslint-disable no-return-assign */
import {
	createContext,
	FC,
	ReactNode,
	useContext,
	useEffect,
	useState,
	useRef,
	useCallback,
	useMemo,
} from 'react';
import { useUpdateUserDevicesMutation } from '@/apollo/gql/generated/graphql';
import { Notification } from 'grommet';
import { uniqBy } from 'lodash';
import BlockedDevices from '@/components/deviceModals/blockedDevices';
import NoDevicesFound from '@/components/deviceModals/noDevicesFound';
import PermissionRequired from '@/components/deviceModals/permissionDismissed';
import useTranslation from 'next-translate/useTranslation';
import { mapDevicesToUser } from '@/helpers/mapDevices';
import { customBugsnagLogger } from '@/utils/bugsnag';
import { isSafari } from 'react-device-detect';
import useUpdateUser from '@/hooks/useUpdateUser';
import { useSubscribeToSignal, SignalTypes } from './useSignal';
import DevicesTempContext from './useTempDevices';

type Props = {
	userId: string;
	children: ReactNode;
};

type ContextType = {
	setDevice: (deviceType: string, deviceId: string) => void;
	toggleDevice: (deviceType: string) => void;
	mic?: MediaDeviceInfo;
	camera?: MediaDeviceInfo;
	availableCameras: MediaDeviceInfo[];
	availableMicrophones: MediaDeviceInfo[];
	availableDevices: MediaDeviceInfo[];
	stream?: MediaStream;
	getStreams: (stopStream?: boolean) => void;
	getDevices: () => void;
	deviceStatus: {
		video: boolean;
		audio: boolean;
	};
	getMediaPermissionStatus: (name: 'camera' | 'microphone') => void;
	permissionsGranted: boolean;
	permissionStatus: string;
};

const Context = createContext<ContextType>(undefined as unknown as ContextType);

const DevicesContext: FC<Props> = ({ children, userId }: Props) => {
	const [showBlockedDevicesModal, setShowBlockedDevicesModal] = useState(false);
	const [showNoDevicesFound, setShowNoDevicesFound] = useState(false);
	const [showPermissionRequired, setPermissionRequired] = useState(false);
	const [permissionsGranted, setPermissionsGranted] = useState(false);
	const [permissionStatus, setPermissionStatus] = useState<string>();
	const { t } = useTranslation('default');
	const { updatePermissions } = useUpdateUser();

	const [deviceSuggestions, setDeviceSuggestions] = useState<{
		camera: MediaDeviceInfo | null;
		microphone: MediaDeviceInfo | null;
	} | null>(null);

	const subscribeToSuggestions = useSubscribeToSignal<any>(
		SignalTypes.SUGGEST_DEVICES
	);

	const [updateUserDevices] = useUpdateUserDevicesMutation({
		variables: {},
	});

	const [availableDevices, setAvailableDevices] = useState<MediaDeviceInfo[]>(
		[]
	);
	const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>(
		[]
	);
	const [availableMicrophones, setAvailableMicrophones] = useState<
		MediaDeviceInfo[]
	>([]);

	const [camera, setCamera] = useState<MediaDeviceInfo>();
	const [mic, setMic] = useState<MediaDeviceInfo>();
	const [stream, setStream] = useState<MediaStream>();
	const [deviceStatus, setDeviceStatus] = useState<{
		video: boolean;
		audio: boolean;
	}>({
		video: true,
		audio: false,
	});
	const isMountedRef = useRef(false);

	useEffect(() => {
		if (isMountedRef.current || !userId) return;
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, [userId]);
	/**
	 * getMediaPermissionStatus
	 * @description: This method check if the permissions were provided by the user
	 * @param name: The media stream to check if permission was provided
	 */
	const getMediaPermissionStatus = useCallback(
		async (name: 'camera' | 'microphone') => {
			customBugsnagLogger.debug('get permission status');

			const handlePermissionQuery = async (permissionQuery) => {
				permissionQuery.addEventListener('change', (devicePermStatus) => {
					const status: PermissionState = devicePermStatus.target?.state || '';
					customBugsnagLogger.debug(`get permission status changed ${status}`);

					setPermissionStatus(status);
					setPermissionsGranted(status === 'granted');
					updatePermissions(status);
					return status;
				});

				setPermissionStatus(permissionQuery.state);
				setPermissionsGranted(permissionQuery.state === 'granted');
				updatePermissions(permissionQuery.state);
				return permissionQuery.state;
			};

			const handlePermissionsWithMediaDevices = async () => {
				try {
					await navigator.mediaDevices.getUserMedia({
						audio: true,
						video: true,
					});
					customBugsnagLogger.debug('getUserMedia success');
					setPermissionStatus('granted');
					setPermissionsGranted(false);
					updatePermissions('granted');
					return 'granted';
				} catch (getUserMediaError) {
					customBugsnagLogger.error(getUserMediaError);
					setPermissionStatus('denied');
					setPermissionsGranted(false);
					updatePermissions('denied');
					return 'denied';
				}
			};

			const checkPermissionsApi = async () => {
				if (navigator.permissions && !isSafari) {
					try {
						await navigator.permissions.query({ name });
						return true;
					} catch (e) {
						return false;
					}
				} else {
					return false;
				}
			};

			try {
				const supportsPermissionsAPI = await checkPermissionsApi();
				if (supportsPermissionsAPI) {
					const permissionPromise = await navigator.permissions.query({ name });
					customBugsnagLogger.debug('supports permission query');
					return await handlePermissionQuery(permissionPromise);
				}
				customBugsnagLogger.debug(
					`Permissions API does not support '${name}', trying getUserMedia`
				);
				await handlePermissionsWithMediaDevices();
			} catch (error) {
				console.error(
					'Error querying permissions or requesting access:',
					error
				);
				customBugsnagLogger.error(error);
				setPermissionStatus('denied');
				setPermissionsGranted(false);
				updatePermissions('denied');
				return 'denied';
			}
		},
		[updatePermissions, setPermissionStatus, setPermissionsGranted]
	);

	const getAvailableDevices = useCallback(async () => {
		console.log('get Available Devices');
		try {
			navigator.mediaDevices.enumerateDevices().then((devices) => {
				const videoDevices = devices.filter(
					(device) => device.kind === 'videoinput'
				);
				const audioDevices = devices.filter(
					(device) => device.kind === 'audioinput'
				);
				setAvailableCameras(videoDevices);
				setAvailableMicrophones(audioDevices);
				const last = localStorage.getItem('cameraId');
				const lastMic = localStorage.getItem('micId');
				if (last) {
					const lastCamera = videoDevices.find((cam) => cam.deviceId === last);
					if (lastCamera) {
						setCamera(lastCamera);
					} else {
						setCamera(videoDevices[0]);
					}
				} else {
					setCamera(videoDevices[0]);
				}
				if (lastMic) {
					const lastMicrophone = audioDevices.find(
						(m) => m.deviceId === lastMic
					);
					if (lastMicrophone) {
						setMic(lastMicrophone);
					} else {
						setMic(audioDevices[0]);
					}
				} else {
					setMic(audioDevices[0]);
				}
				console.log(devices);
				setAvailableDevices(devices);
			});
		} catch (error) {
			customBugsnagLogger.error(error);
			console.log(error);
		}
	}, []);

	const checkPermissions = useCallback(async () => {
		console.log('CHECKING PERMISSIONS');
		getAvailableDevices();
	}, [getAvailableDevices]);

	const getDevices = checkPermissions;

	const getStreams = useCallback(
		async (stopStream = true) => {
			console.log('GET STREAMS');
			try {
				if (availableDevices.length === 0) {
					return getAvailableDevices();
				}
				const cameraId = localStorage.getItem('cameraId') || camera?.deviceId;
				const micId = mic?.deviceId;
				const constraints = cameraId
					? {
							video: {
								deviceId: { ideal: cameraId },
							},
							audio: {
								deviceId: { ideal: micId },
							},
					  }
					: { video: true, audio: true };
				await navigator?.mediaDevices
					.getUserMedia(constraints)
					.then(async (result) => {
						// mute audio
						result
							.getAudioTracks()
							.forEach(
								(track: MediaStreamTrack) =>
									(track.enabled = deviceStatus.audio)
							);
						setStream(result);
						if (stopStream) {
							console.log('stoping stream');
							result.getTracks().forEach((track) => track.stop());
						}
					})
					.catch((e) => {
						customBugsnagLogger.debug('get streams error');
						switch (e.message) {
							case 'Permission denied':
								// Permissions might get denied between the time we check permissions and get the devices.
								customBugsnagLogger.warn(e);
								setShowBlockedDevicesModal(true);
								break;

							case 'Permission dismissed':
								customBugsnagLogger.warn(e);
								setPermissionRequired(true);
								break;

							case 'Permission denied by system':
								customBugsnagLogger.warn(e);
								setShowBlockedDevicesModal(true);
								break;

							default:
								customBugsnagLogger.error(e);
								setShowNoDevicesFound(true);
						}
					})
					.finally(() => {
						console.log('getting camera permissions');
						getMediaPermissionStatus('camera');
					});
			} catch (e) {
				console.log(e);
				customBugsnagLogger.error(e);
			}
		},
		[
			availableDevices.length,
			camera?.deviceId,
			deviceStatus.audio,
			getAvailableDevices,
			getMediaPermissionStatus,
			mic?.deviceId,
		]
	);

	const stopStream = useCallback(() => {
		if (stream) {
			stream.getTracks().forEach((track) => {
				track.enabled = false;
				track.stop();
			});
			setStream(undefined);
		}
	}, [stream]);

	const stopStreamByType = useCallback(
		(streamType: 'audio' | 'video') => {
			if (stream) {
				stream
					.getTracks()
					.filter((element) => element.kind === streamType)
					.forEach((track) => {
						track.enabled = false;
						track.stop();
					});
				setStream(undefined);
			}
		},
		[stream]
	);

	useEffect(() => {
		window.addEventListener('beforeunload', stopStream);
		return () => {
			window.removeEventListener('beforeunload', stopStream);
			// if (!isMountedRef.current) {
			// 	stopStream();
			// }
		};
	}, [stopStream]);

	useEffect(() => {
		if (!permissionsGranted) {
			checkPermissions();
		}
	}, [checkPermissions, permissionsGranted]);

	useEffect(() => {
		if (!userId || !camera) return;
		const uniqueDev = uniqBy(
			mapDevicesToUser(availableDevices, camera, mic),
			'id'
		);

		updateUserDevices({
			variables: {
				input: { userId, devices: uniqueDev },
			},
		});
	}, [availableDevices, camera, mic, updateUserDevices, userId]);

	useEffect(
		() =>
			subscribeToSuggestions('*', (suggestions) => {
				const cameraSuggestion = suggestions?.videoinput
					? availableCameras.find(
							(cam) => cam.deviceId === suggestions?.videoinput
					  )
					: null;
				const microphoneSuggestion = suggestions?.audioinput
					? availableMicrophones.find(
							(m) => m.deviceId === suggestions?.audioinput
					  )
					: null;

				setDeviceSuggestions({
					camera: cameraSuggestion,
					microphone: microphoneSuggestion,
				});
			}),
		[availableCameras, availableMicrophones, subscribeToSuggestions]
	);

	const values = useMemo(() => {
		const toggleDevice = (deviceType: string) => {
			const newStatus = !deviceStatus[deviceType];
			if (deviceType === 'video') {
				if (newStatus) {
					getStreams(false);
				} else {
					stopStreamByType('video');
				}

				setDeviceStatus({ video: newStatus, audio: deviceStatus.audio });
			}
			if (deviceType === 'audio') {
				stopStreamByType('audio');
				setDeviceStatus({ audio: newStatus, video: deviceStatus.video });
			}
		};

		const setDevice = (deviceType: string, deviceId: string) => {
			const newPreferred = availableDevices.find(
				(device) => device.deviceId === deviceId
			);
			if (newPreferred) {
				if (deviceType === 'video') {
					setCamera(newPreferred);
					localStorage.setItem('cameraId', newPreferred?.deviceId);
					// Fetch streams again and reset the selected stream
					getStreams(true);
				} else {
					setMic(newPreferred);
					localStorage.setItem('micId', newPreferred?.deviceId);
				}
			}
		};

		return {
			availableDevices,
			availableCameras,
			availableMicrophones,
			camera,
			mic,
			toggleDevice,
			setDevice,
			stream,
			deviceStatus,
			getStreams,
			getDevices,
			getMediaPermissionStatus,
			permissionsGranted,
			permissionStatus,
		};
	}, [
		availableDevices,
		availableCameras,
		availableMicrophones,
		camera,
		mic,
		stream,
		deviceStatus,
		getStreams,
		getDevices,
		getMediaPermissionStatus,
		permissionsGranted,
		stopStreamByType,
		permissionStatus,
	]);

	const applySuggestions = () => {
		if (deviceSuggestions?.camera) setCamera(deviceSuggestions?.camera);
		if (deviceSuggestions?.microphone) setMic(deviceSuggestions?.microphone);
		setDeviceSuggestions(null);
	};
	return (
		<Context.Provider
			// eslint-disable-next-line react/jsx-no-constructed-context-values
			value={values}
		>
			{deviceSuggestions && (
				<Notification
					status="warning"
					message={`${t('settings_modal.suggestions_received')} 
					${
						deviceSuggestions.camera
							? `Camera: ${deviceSuggestions?.camera?.label} `
							: ''
					}  
					${
						deviceSuggestions.microphone
							? `Microphone: ${deviceSuggestions?.microphone?.label} `
							: ''
					}`}
					onClose={() => setDeviceSuggestions(null)}
					actions={[
						{
							href: '#',
							label: t('settings_modal.apply'),
							onClick: applySuggestions,
						},
					]}
					global
				/>
			)}
			{showBlockedDevicesModal && (
				<BlockedDevices setShow={setShowBlockedDevicesModal} />
			)}
			{showNoDevicesFound && <NoDevicesFound setShow={setShowNoDevicesFound} />}
			{showPermissionRequired && (
				<PermissionRequired setShow={setPermissionRequired} />
			)}
			<DevicesTempContext userId={userId}>{children} </DevicesTempContext>
		</Context.Provider>
	);
};

export const useDevicesContext = (): ContextType => useContext(Context);

export default DevicesContext;
